import { useEffect, useState, useRef } from 'react';
import { useOutletContext, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  getCalls, getDepartments, getComplianceFilters, getUsers, uploadCallFile, 
  logActivity, getCallStatistics, getCallAudioUrl,
  getUnknownCalls, getUnknownCallsStats, syncUnknownCallsWithUsers, assignCallToUser, markCallAsUnknown,
  queueCallsForReprocess, startReprocessing, regenerateTranscript,
  findDuplicateCalls, cleanupDuplicateCalls,
  softDeleteCalls, getDeletedCalls, restoreDeletedCalls, permanentDeleteCalls, getDeletedCallsArchivedStats,
  refreshCallTypes, getScoringConfigs
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Search, 
  Filter, 
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Phone,
  Clock,
  AlertTriangle,
  Star,
  ShieldAlert,
  Info,
  Calendar,
  X,
  Upload,
  FileAudio,
  Loader2,
  CheckCircle2,
  FileText,
  MessageSquare,
  Play,
  Pause,
  BarChart3,
  TrendingUp,
  Headphones,
  Users,
  Building2,
  ArrowUpDown,
  UserX,
  RefreshCw,
  UserPlus,
  HelpCircle,
  FileSearch,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneForwarded,
  Copy,
  Trash2,
  Archive
} from 'lucide-react';
import { toast } from 'sonner';

// Get call type icon and styling
const getCallTypeIcon = (callType, detectedCallType) => {
  const type = ((callType || detectedCallType) || '').toLowerCase();
  if (type.includes('inbound') || type === 'incoming') {
    return { 
      icon: PhoneIncoming, 
      color: 'text-amber-500', 
      bg: 'bg-amber-100 dark:bg-amber-900/30',
      label: 'Inbound'
    };
  } else if (type.includes('outbound') || type === 'outgoing') {
    return { 
      icon: PhoneOutgoing, 
      color: 'text-green-500', 
      bg: 'bg-green-100 dark:bg-green-900/30',
      label: 'Outbound'
    };
  } else if (type.includes('intra') || type.includes('pbx') || type.includes('internal')) {
    return { 
      icon: PhoneForwarded, 
      color: 'text-red-500', 
      bg: 'bg-red-100 dark:bg-red-900/30',
      label: 'Intra PBX'
    };
  }
  return { 
    icon: Phone, 
    color: 'text-muted-foreground', 
    bg: 'bg-muted dark:bg-slate-800',
    label: 'Unknown'
  };
};

// Generate month options for filter
const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleString('default', { month: 'long', year: 'numeric' })
    });
  }
  return months;
};

const MONTH_OPTIONS = getMonthOptions();

const ALERT_LEVELS = [
  { value: 'all', label: 'All Levels' },
  { value: 'critical', label: 'Critical' },
  { value: 'high_alert', label: 'High Alert' },
  { value: 'medium', label: 'Medium' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'good', label: 'Good' },
  { value: 'has_compliance', label: '⚠️ Has Compliance Alerts' },
];

const CLASSIFICATIONS = [
  { value: 'all', label: 'All Classifications' },
  { value: 'best', label: 'Best' },
  { value: 'good', label: 'Good' },
  { value: 'improvement', label: 'Improvement' },
  { value: 'high_alert', label: 'High Alert' },
];

const CALL_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'introduction', label: 'Introduction' },
  { value: 'screening', label: 'Screening' },
  { value: 'interview', label: 'Interview' },
  { value: 'followup', label: 'Follow-up' },
  { value: 'client', label: 'Client' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'other', label: 'Other' },
];

export default function AdminCalls() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const adminUrl = outletContext.adminUrl;
  const tenant = outletContext.tenant;
  const user = outletContext.user;
  const tenantId = tenant?.id || tenant?._id || '';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get initial tab from URL parameter, default to 'calls'
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'unknown') return 'unknown';
    if (tabParam === 'deleted') return 'deleted';
    if (tabParam === 'stats' || tabParam === 'statistics') return 'statistics';
    if (tabParam === 'calls') return 'calls';
    return 'statistics';  // Default to statistics tab
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [calls, setCalls] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [complianceFilters, setComplianceFilters] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  // Update activeTab when URL search params change
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'unknown' && activeTab !== 'unknown') {
      setActiveTab('unknown');
    } else if (tabParam === 'deleted' && activeTab !== 'deleted') {
      setActiveTab('deleted');
    } else if ((tabParam === 'stats' || tabParam === 'statistics') && activeTab !== 'statistics') {
      setActiveTab('statistics');
    } else if (tabParam === 'calls' && activeTab !== 'calls') {
      setActiveTab('calls');
    }
  }, [searchParams]);
  
  // Unknown Calls state
  const [unknownCalls, setUnknownCalls] = useState([]);
  const [unknownCallsStats, setUnknownCallsStats] = useState(null);
  const [unknownCallsTotal, setUnknownCallsTotal] = useState(0);
  const [isLoadingUnknown, setIsLoadingUnknown] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [callToAssign, setCallToAssign] = useState(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [selectedUnknownCalls, setSelectedUnknownCalls] = useState([]);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [bulkAssignUserId, setBulkAssignUserId] = useState('');
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  
  // Unknown Calls Sort & Filter state
  const [unknownSortField, setUnknownSortField] = useState('call_date');
  const [unknownSortOrder, setUnknownSortOrder] = useState('desc');
  const [unknownFilters, setUnknownFilters] = useState({
    search: '',
    callType: 'all',
    classification: 'all',
    dateFrom: '',
    dateTo: '',
    minScore: '',
    maxScore: ''
  });
  const [showUnknownFilters, setShowUnknownFilters] = useState(false);
  
  // Multi-select for main calls list
  const [selectedCalls, setSelectedCalls] = useState([]);
  const [mainBulkAssignDialogOpen, setMainBulkAssignDialogOpen] = useState(false);
  const [mainBulkAssignUserId, setMainBulkAssignUserId] = useState('');
  const [mainBulkAction, setMainBulkAction] = useState('assign'); // 'assign', 'unknown', or 'reprocess'
  const [isMainBulkAssigning, setIsMainBulkAssigning] = useState(false);
  
  // Reprocess state
  const [isReprocessing, setIsReprocessing] = useState(false);
  
  // Regenerate Transcript state
  const [isRegeneratingTranscript, setIsRegeneratingTranscript] = useState(false);
  
  // Statistics state
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsSortField, setStatsSortField] = useState('date');
  const [statsSortOrder, setStatsSortOrder] = useState('desc');
  
  // Audio player state
  const [playingCallId, setPlayingCallId] = useState(null);
  const audioRef = useRef(null);
  
  // Quick view dialogs
  const [showTranscript, setShowTranscript] = useState(null);
  const [showFeedback, setShowFeedback] = useState(null);
  const [showScore, setShowScore] = useState(null);
  const [showTranscriptHistory, setShowTranscriptHistory] = useState(false);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(0);
  
  // Upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const fileInputRef = useRef(null);
  
  // Duplicate cleanup state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
  
  // Reprocess dialog state
  const [showReprocessDialog, setShowReprocessDialog] = useState(false);
  const [scoringGuides, setScoringGuides] = useState([]);
  const [selectedScoringGuide, setSelectedScoringGuide] = useState('');
  const [reprocessOptions, setReprocessOptions] = useState({
    reprocess_transcript: true,
    reprocess_analysis: true
  });
  
  // Filters for Call Management
  const [filters, setFilters] = useState({
    search: '',
    alert_level: 'all',
    classification: 'all',
    call_type: 'all',
    department_id: 'all',
    compliance_filter: 'all',
    date_from: '',
    date_to: '',
    month: 'all',
    source: 'all',  // 'all', 'synced', 'manual_upload'
    is_reprocessed: false,  // Filter for reprocessed calls only
    assigned_only: false,  // For auditors - show only assigned users' calls
  });
  
  // Assigned users state for auditors
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [assignedUsersCount, setAssignedUsersCount] = useState(0);
  
  // Deleted Calls state
  const [deletedCalls, setDeletedCalls] = useState([]);
  const [deletedCallsTotal, setDeletedCallsTotal] = useState(0);
  const [isLoadingDeleted, setIsLoadingDeleted] = useState(false);
  const [deletedCurrentPage, setDeletedCurrentPage] = useState(1);
  const [deletedTotalPages, setDeletedTotalPages] = useState(1);
  const [deletedSearch, setDeletedSearch] = useState('');
  const [selectedDeletedCalls, setSelectedDeletedCalls] = useState([]);
  const [archivedStats, setArchivedStats] = useState(null);
  
  // Delete Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isRefreshingCallTypes, setIsRefreshingCallTypes] = useState(false);
  
  // Sorting state for calls table
  const [callSortField, setCallSortField] = useState('call_date');
  const [callSortOrder, setCallSortOrder] = useState('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [unknownCurrentPage, setUnknownCurrentPage] = useState(1);
  const [unknownTotalPages, setUnknownTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 100;

  // Filters for Statistics
  const [statsFilters, setStatsFilters] = useState({
    search: '',
    user_id: 'all',
    department_id: 'all',
    month: 'all',
    date_from: '',
    date_to: '',
  });

  const fetchCalls = async (page = currentPage) => {
    setIsLoading(true);
    try {
      const params = {
        page: page,
        limit: ITEMS_PER_PAGE,
        sort_by: callSortField,
        sort_order: callSortOrder
      };
      
      // Server-side search
      if (filters.search) params.search = filters.search;
      
      // Source filter (synced vs manual)
      if (filters.source !== 'all') params.source = filters.source;
      
      if (filters.alert_level !== 'all') {
        if (filters.alert_level === 'has_compliance') {
          params.has_compliance_alerts = true;
        } else {
          params.alert_level = filters.alert_level;
        }
      }
      if (filters.classification !== 'all') params.classification = filters.classification;
      if (filters.call_type !== 'all') params.call_type = filters.call_type;
      if (filters.department_id !== 'all') params.department_id = filters.department_id;
      if (filters.compliance_filter !== 'all') params.compliance_filter = filters.compliance_filter;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.is_reprocessed) params.is_reprocessed = true;
      
      if (filters.month !== 'all') {
        const [year, month] = filters.month.split('-');
        const startDate = new Date(year, parseInt(month) - 1, 1);
        const endDate = new Date(year, parseInt(month), 0);
        params.date_from = startDate.toISOString().split('T')[0];
        params.date_to = endDate.toISOString().split('T')[0];
      }
      
      const { data } = await getCalls(params);
      setCalls(data.calls);
      setTotal(data.total);
      setTotalPages(data.total_pages || Math.ceil(data.total / ITEMS_PER_PAGE));
      setCurrentPage(data.page || page);
    } catch (error) {
      console.error('Failed to fetch calls:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatistics = async () => {
    setStatsLoading(true);
    try {
      const params = {};
      if (statsFilters.user_id !== 'all') params.user_id = statsFilters.user_id;
      if (statsFilters.department_id !== 'all') params.department_id = statsFilters.department_id;
      
      const { data } = await getCallStatistics(params);
      setStatistics(data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchUnknownCalls = async (page = unknownCurrentPage) => {
    setIsLoadingUnknown(true);
    try {
      // Build query params with sort and filters
      const params = { 
        page: page, 
        limit: ITEMS_PER_PAGE,
        sort_field: unknownSortField,
        sort_order: unknownSortOrder
      };
      
      // Add filter params if set
      if (unknownFilters.search) params.search = unknownFilters.search;
      if (unknownFilters.callType !== 'all') params.call_type = unknownFilters.callType;
      if (unknownFilters.classification !== 'all') params.classification = unknownFilters.classification;
      if (unknownFilters.dateFrom) params.date_from = unknownFilters.dateFrom;
      if (unknownFilters.dateTo) params.date_to = unknownFilters.dateTo;
      if (unknownFilters.minScore) params.min_score = unknownFilters.minScore;
      if (unknownFilters.maxScore) params.max_score = unknownFilters.maxScore;
      
      const [callsRes, statsRes] = await Promise.all([
        getUnknownCalls(params),
        getUnknownCallsStats()
      ]);
      setUnknownCalls(callsRes.data.calls || []);
      setUnknownCallsTotal(callsRes.data.total || 0);
      setUnknownTotalPages(Math.ceil((callsRes.data.total || 0) / ITEMS_PER_PAGE));
      setUnknownCurrentPage(page);
      setUnknownCallsStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch unknown calls:', error);
    } finally {
      setIsLoadingUnknown(false);
    }
  };
  
  // Handle unknown calls sort
  const handleUnknownSort = (field) => {
    if (unknownSortField === field) {
      setUnknownSortOrder(unknownSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setUnknownSortField(field);
      setUnknownSortOrder('desc');
    }
  };
  
  // Load scoring guides for reprocess dialog
  const loadScoringGuides = async () => {
    try {
      const { data } = await getScoringConfigs();
      setScoringGuides(data.configs || []);
      // Set default guide
      const defaultGuide = (data.configs || []).find(g => g.is_default);
      if (defaultGuide) {
        setSelectedScoringGuide(defaultGuide.id);
      }
    } catch (error) {
      console.error('Failed to load scoring guides:', error);
    }
  };
  
  // Open reprocess dialog
  const openReprocessDialog = async () => {
    if (selectedCalls.length === 0) {
      toast.error('Please select calls to reprocess');
      return;
    }
    
    // Check reprocess count for selected calls
    const selectedCallsData = calls.filter(c => selectedCalls.includes(c.id));
    const overLimitCalls = selectedCallsData.filter(c => (c.reprocess_count || 0) >= 3);
    
    if (overLimitCalls.length > 0) {
      toast.error(`${overLimitCalls.length} call(s) have already been reprocessed 3 times (maximum limit)`);
      return;
    }
    
    await loadScoringGuides();
    setShowReprocessDialog(true);
  };
  
  // Handle unknown calls filter change
  const handleUnknownFilterChange = (key, value) => {
    setUnknownFilters(prev => ({ ...prev, [key]: value }));
  };
  
  // Clear unknown filters
  const clearUnknownFilters = () => {
    setUnknownFilters({
      search: '',
      callType: 'all',
      classification: 'all',
      dateFrom: '',
      dateTo: '',
      minScore: '',
      maxScore: ''
    });
  };
  
  // Effect to refetch when sort/filter changes
  useEffect(() => {
    if (activeTab === 'unknown') {
      fetchUnknownCalls(1);
    }
  }, [unknownSortField, unknownSortOrder, unknownFilters]);

  // Fetch deleted calls
  const fetchDeletedCalls = async (page = deletedCurrentPage) => {
    setIsLoadingDeleted(true);
    try {
      const params = {
        page,
        limit: ITEMS_PER_PAGE,
        sort_by: 'deleted_at',
        sort_order: 'desc'
      };
      if (deletedSearch) params.search = deletedSearch;
      
      const [callsRes, statsRes] = await Promise.all([
        getDeletedCalls(params),
        getDeletedCallsArchivedStats()
      ]);
      
      setDeletedCalls(callsRes.data.calls || []);
      setDeletedCallsTotal(callsRes.data.total || 0);
      setDeletedTotalPages(Math.ceil((callsRes.data.total || 0) / ITEMS_PER_PAGE));
      setDeletedCurrentPage(page);
      setArchivedStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching deleted calls:', error);
      toast.error('Failed to load deleted calls');
    } finally {
      setIsLoadingDeleted(false);
    }
  };

  // Effect to fetch deleted calls when tab changes
  useEffect(() => {
    if (activeTab === 'deleted') {
      fetchDeletedCalls(1);
    }
  }, [activeTab, deletedSearch]);

  // Handle delete calls
  const handleDeleteCalls = async () => {
    if (selectedCalls.length === 0 || !deleteReason.trim()) {
      toast.error('Please select calls and provide a reason for deletion');
      return;
    }
    
    setIsDeleting(true);
    try {
      const res = await softDeleteCalls(selectedCalls, deleteReason.trim());
      const archived = res.data.archived_related_data || {};
      const totalArchived = Object.values(archived).reduce((a, b) => a + b, 0);
      
      let message = res.data.message;
      if (totalArchived > 0) {
        message += ` (${totalArchived} related records archived)`;
      }
      toast.success(message);
      setDeleteDialogOpen(false);
      setDeleteReason('');
      setSelectedCalls([]);
      fetchCalls();
      if (activeTab === 'deleted') {
        fetchDeletedCalls();
      }
    } catch (error) {
      toast.error('Failed to delete calls: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle restore deleted calls
  const handleRestoreCalls = async () => {
    if (selectedDeletedCalls.length === 0) {
      toast.error('Please select calls to restore');
      return;
    }
    
    setIsRestoring(true);
    try {
      const res = await restoreDeletedCalls(selectedDeletedCalls);
      const restored = res.data.restored_related_data || {};
      const totalRestored = Object.values(restored).reduce((a, b) => a + b, 0);
      
      let message = res.data.message;
      if (totalRestored > 0) {
        message += ` (${totalRestored} related records restored)`;
      }
      toast.success(message);
      setSelectedDeletedCalls([]);
      fetchDeletedCalls();
      fetchCalls();
    } catch (error) {
      toast.error('Failed to restore calls');
    } finally {
      setIsRestoring(false);
    }
  };

  // Handle permanent delete
  const handlePermanentDelete = async () => {
    if (selectedDeletedCalls.length === 0) {
      toast.error('Please select calls to permanently delete');
      return;
    }
    
    if (!confirm(`Are you sure you want to PERMANENTLY delete ${selectedDeletedCalls.length} call(s) and ALL their related data (QA notes, alerts, review requests, audit trail)? This cannot be undone.`)) {
      return;
    }
    
    try {
      const res = await permanentDeleteCalls(selectedDeletedCalls);
      const counts = res.data.deleted_counts || {};
      const totalDeleted = res.data.total_records_deleted || 0;
      
      let message = res.data.message;
      if (totalDeleted > res.data.deleted_count) {
        message += ` (${totalDeleted} total records removed)`;
      }
      toast.success(message);
      setSelectedDeletedCalls([]);
      fetchDeletedCalls();
    } catch (error) {
      toast.error('Failed to permanently delete calls');
    }
  };

  // Handle refresh call types
  const handleRefreshCallTypes = async () => {
    if (selectedCalls.length === 0) {
      toast.error('Please select calls to refresh call types');
      return;
    }
    
    setIsRefreshingCallTypes(true);
    try {
      const res = await refreshCallTypes(selectedCalls);
      toast.success(res.data.message);
      if (res.data.skipped > 0) {
        toast.info(`${res.data.skipped} calls skipped (no transcript available)`);
      }
      // Clear selection and refresh after a delay to allow background processing
      setSelectedCalls([]);
      setTimeout(() => {
        fetchCalls();
      }, 2000);
    } catch (error) {
      toast.error('Failed to refresh call types: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setIsRefreshingCallTypes(false);
    }
  };

  const handleSyncCalls = async () => {
    setIsSyncing(true);
    try {
      const response = await syncUnknownCallsWithUsers();
      toast.success(`Synced ${response.data.matched_count} calls with users!`);
      // Refresh both call lists
      fetchCalls();
      fetchUnknownCalls();
    } catch (error) {
      toast.error('Failed to sync calls');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAssignCall = async () => {
    if (!callToAssign || !assignUserId) return;
    
    try {
      const response = await assignCallToUser(callToAssign.id, assignUserId);
      toast.success(response.data.message);
      setAssignDialogOpen(false);
      setCallToAssign(null);
      setAssignUserId('');
      fetchUnknownCalls();
      fetchCalls();
    } catch (error) {
      toast.error('Failed to assign call');
    }
  };

  // Bulk assign multiple unknown calls to a user
  const handleBulkAssignCalls = async () => {
    if (selectedUnknownCalls.length === 0 || !bulkAssignUserId) return;
    
    setIsBulkAssigning(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const callId of selectedUnknownCalls) {
      try {
        await assignCallToUser(callId, bulkAssignUserId);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    setIsBulkAssigning(false);
    setBulkAssignDialogOpen(false);
    setSelectedUnknownCalls([]);
    setBulkAssignUserId('');
    
    if (successCount > 0) {
      toast.success(`Successfully assigned ${successCount} call(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to assign ${failCount} call(s)`);
    }
    
    fetchUnknownCalls();
    fetchCalls();
  };

  // Main calls bulk assignment handler
  const handleMainBulkAssign = async () => {
    if (selectedCalls.length === 0) return;
    
    setIsMainBulkAssigning(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const callId of selectedCalls) {
      try {
        if (mainBulkAction === 'unknown') {
          await markCallAsUnknown(callId);
        } else if (mainBulkAssignUserId) {
          await assignCallToUser(callId, mainBulkAssignUserId);
        }
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    setIsMainBulkAssigning(false);
    setMainBulkAssignDialogOpen(false);
    setSelectedCalls([]);
    setMainBulkAssignUserId('');
    setMainBulkAction('assign');
    
    if (successCount > 0) {
      toast.success(`Successfully ${mainBulkAction === 'unknown' ? 'marked' : 'assigned'} ${successCount} call(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to process ${failCount} call(s)`);
    }
    
    fetchUnknownCalls();
    fetchCalls();
  };

  // Reprocess selected calls handler
  const handleReprocessCalls = async () => {
    if (selectedCalls.length === 0) return;
    
    setIsReprocessing(true);
    try {
      const data = {
        tenant_id: tenantId,
        call_ids: selectedCalls,
        all_calls: false,
        reprocess_transcript: reprocessOptions.reprocess_transcript,
        reprocess_analysis: reprocessOptions.reprocess_analysis,
        scoring_guide_id: selectedScoringGuide || null
      };
      
      // Queue the calls
      const queueResponse = await queueCallsForReprocess(tenantId, data, user?.id);
      
      // Automatically start processing
      await startReprocessing(tenantId, user?.id);
      
      const guideName = scoringGuides.find(g => g.id === selectedScoringGuide)?.name || 'Default';
      toast.success(`${selectedCalls.length} call(s) queued for reprocessing with "${guideName}" scoring guide`);
      
      // Store selected call IDs for polling
      const processingCallIds = [...selectedCalls];
      setSelectedCalls([]);
      setShowReprocessDialog(false);
      
      // Start polling for status updates
      startReprocessPolling(processingCallIds);
    } catch (error) {
      console.error('Error reprocessing calls:', error);
      toast.error('Failed to queue calls for reprocessing');
    } finally {
      setIsReprocessing(false);
    }
  };
  
  // Polling for reprocessing status
  const startReprocessPolling = (callIds) => {
    let pollCount = 0;
    const maxPolls = 60; // Poll for max 5 minutes (5 sec intervals)
    
    const pollInterval = setInterval(async () => {
      pollCount++;
      
      try {
        // Fetch current calls to check status
        const { data } = await getCalls({ limit: 100, call_ids: callIds.join(',') });
        const updatedCalls = data.calls || [];
        
        // Check if any calls are still processing
        const stillProcessing = updatedCalls.filter(c => 
          c.processing_status === 'reprocessing' || c.processing_status === 'queued'
        );
        
        // Check for completed calls
        const completed = updatedCalls.filter(c => 
          c.processing_status !== 'reprocessing' && c.processing_status !== 'queued'
        );
        
        // Refresh the calls list
        fetchCalls(currentPage);
        
        // If all completed or max polls reached, stop polling
        if (stillProcessing.length === 0 || pollCount >= maxPolls) {
          clearInterval(pollInterval);
          
          if (completed.length > 0) {
            toast.success(`${completed.length} call(s) reprocessing completed!`, {
              duration: 5000,
              action: {
                label: 'View',
                onClick: () => setFilters({...filters, is_reprocessed: true})
              }
            });
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
        }
      }
    }, 5000); // Poll every 5 seconds
    
    // Cleanup on component unmount
    return () => clearInterval(pollInterval);
  };

  // Regenerate Transcript handler
  const handleRegenerateTranscript = async () => {
    if (selectedCalls.length === 0) return;
    
    setIsRegeneratingTranscript(true);
    try {
      const response = await regenerateTranscript(selectedCalls, user?.id || 'system');
      toast.success(`${response.data.count} call(s) queued for transcript regeneration`);
      
      // Store call IDs for polling
      const processingCallIds = [...selectedCalls];
      setSelectedCalls([]);
      
      // Start polling for status updates
      startReprocessPolling(processingCallIds);
    } catch (error) {
      console.error('Error regenerating transcripts:', error);
      toast.error('Failed to queue calls for transcript regeneration');
    } finally {
      setIsRegeneratingTranscript(false);
    }
  };

  // Toggle selection of a call in main list
  const toggleCallSelection = (callId) => {
    setSelectedCalls(prev => 
      prev.includes(callId) 
        ? prev.filter(id => id !== callId)
        : [...prev, callId]
    );
  };

  // Select/deselect all calls in current view
  const toggleSelectAllCalls = () => {
    if (selectedCalls.length === calls.length) {
      setSelectedCalls([]);
    } else {
      setSelectedCalls(calls.map(c => c.id));
    }
  };

  // Toggle selection of unknown call
  const toggleUnknownCallSelection = (callId) => {
    setSelectedUnknownCalls(prev => 
      prev.includes(callId) 
        ? prev.filter(id => id !== callId)
        : [...prev, callId]
    );
  };

  // Select all unknown calls
  const toggleSelectAllUnknownCalls = () => {
    if (selectedUnknownCalls.length === unknownCalls.length) {
      setSelectedUnknownCalls([]);
    } else {
      setSelectedUnknownCalls(unknownCalls.map(c => c.id));
    }
  };

  const handleMarkAsUnknown = async (callId) => {
    try {
      await markCallAsUnknown(callId);
      toast.success('Call marked as unknown');
      fetchCalls();
      fetchUnknownCalls();
    } catch (error) {
      toast.error('Failed to mark call');
    }
  };

  useEffect(() => {
    getDepartments().then(res => setDepartments(res.data));
    getComplianceFilters().then(res => setComplianceFilters(res.data)).catch(() => {});
    getUsers().then(res => setUsers(res.data));
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filters change
    fetchCalls(1);
  }, [filters.alert_level, filters.classification, filters.call_type, filters.department_id, filters.compliance_filter, filters.date_from, filters.date_to, filters.month, filters.search, filters.source]);

  // Refetch when sorting changes
  useEffect(() => {
    fetchCalls(currentPage);
  }, [callSortField, callSortOrder]);

  useEffect(() => {
    fetchStatistics();
  }, [statsFilters.user_id, statsFilters.department_id]);

  useEffect(() => {
    if (activeTab === 'unknown') {
      fetchUnknownCalls(1);
    }
  }, [activeTab]);

  // Handle sorting
  const handleSort = (field) => {
    if (callSortField === field) {
      setCallSortOrder(callSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setCallSortField(field);
      setCallSortOrder('desc');
    }
  };

  const renderSortIcon = (field) => {
    if (callSortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return callSortOrder === 'asc' ? 
      <ChevronUp className="h-3 w-3 ml-1" /> : 
      <ChevronDown className="h-3 w-3 ml-1" />;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const clearDateFilters = () => {
    setFilters({...filters, date_from: '', date_to: '', month: 'all'});
  };

  const clearStatsDateFilters = () => {
    setStatsFilters({...statsFilters, date_from: '', date_to: '', month: 'all'});
  };

  // File upload handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return ['mp3', 'wav', 'm4a', 'ogg', 'flac', 'wma', 'aac'].includes(ext);
    });
    if (validFiles.length !== files.length) {
      toast.warning('Some files were skipped. Only audio files are allowed.');
    }
    setUploadFiles(validFiles);
    setUploadProgress(validFiles.map(() => ({ status: 'pending', progress: 0 })));
  };

  const handleUploadCalls = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Please select at least one audio file');
      return;
    }
    setIsUploading(true);
    const results = [];
    let duplicatesSkipped = 0;

    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i];
      setUploadProgress(prev => prev.map((p, idx) => idx === i ? { status: 'uploading', progress: 50 } : p));
      try {
        const userId = selectedUserId && selectedUserId !== 'unassigned' ? selectedUserId : null;
        const response = await uploadCallFile(file, userId);
        results.push({ file: file.name, success: true, callId: response.data.call_id });
        setUploadProgress(prev => prev.map((p, idx) => idx === i ? { status: 'complete', progress: 100 } : p));
      } catch (error) {
        // Check if it's a duplicate error (409 Conflict)
        if (error.response?.status === 409) {
          const detail = error.response?.data?.detail;
          duplicatesSkipped++;
          results.push({ 
            file: file.name, 
            success: false, 
            error: 'Duplicate - already exists',
            isDuplicate: true,
            existingCallId: detail?.call_id
          });
          setUploadProgress(prev => prev.map((p, idx) => idx === i ? { status: 'duplicate', progress: 100 } : p));
        } else {
          results.push({ file: file.name, success: false, error: error.message });
          setUploadProgress(prev => prev.map((p, idx) => idx === i ? { status: 'error', progress: 100 } : p));
        }
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success && !r.isDuplicate).length;
    
    if (successCount > 0) toast.success(`${successCount} call(s) uploaded successfully. AI analysis in progress.`);
    if (duplicatesSkipped > 0) toast.warning(`${duplicatesSkipped} file(s) skipped - already uploaded previously.`);
    if (failCount > 0) toast.error(`${failCount} call(s) failed to upload.`);

    setTimeout(() => {
      setIsUploading(false);
      setShowUploadDialog(false);
      setUploadFiles([]);
      setUploadProgress([]);
      setSelectedUserId('');
      fetchCalls();
      fetchStatistics();
    }, 1500);
  };

  const removeFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => prev.filter((_, i) => i !== index));
  };

  const resetUploadDialog = () => {
    setShowUploadDialog(false);
    setUploadFiles([]);
    setUploadProgress([]);
    setSelectedUserId('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Duplicate cleanup functions
  const handleCheckDuplicates = async () => {
    setCheckingDuplicates(true);
    try {
      const { data } = await findDuplicateCalls(tenant?.id);
      setDuplicateData(data);
      setShowDuplicateDialog(true);
    } catch (error) {
      console.error('Error checking duplicates:', error);
      toast.error('Failed to check for duplicates');
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleCleanupDuplicates = async (dryRun = false) => {
    setCleaningDuplicates(true);
    try {
      const { data } = await cleanupDuplicateCalls(tenant?.id, dryRun);
      
      if (dryRun) {
        setDuplicateData(prev => ({
          ...prev,
          preview: data.preview,
          calls_to_delete: data.calls_to_delete
        }));
        toast.info(`Found ${data.calls_to_delete} duplicate calls that can be cleaned up`);
      } else {
        toast.success(`Successfully removed ${data.deleted_count} duplicate calls`);
        setShowDuplicateDialog(false);
        setDuplicateData(null);
        loadCalls(); // Refresh the calls list
      }
    } catch (error) {
      console.error('Error cleaning duplicates:', error);
      toast.error('Failed to cleanup duplicates');
    } finally {
      setCleaningDuplicates(false);
    }
  };

  // Client-side search filter only (sorting is done server-side)
  // Since search is now server-side, we don't need client-side filtering
  // Just use the calls directly from the server response
  const filteredCalls = calls;

  // Toggle sort for calls table
  const toggleCallSort = (field) => {
    if (callSortField === field) {
      setCallSortOrder(callSortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setCallSortField(field);
      setCallSortOrder('desc');
    }
  };

  // Sortable header component
  const SortableHeader = ({ field, children, className = '' }) => (
    <TableHead 
      className={`cursor-pointer hover:bg-muted select-none ${className}`}
      onClick={() => toggleCallSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {callSortField === field ? (
          callSortOrder === 'asc' ? 
            <ChevronUp className="h-4 w-4 text-blue-600" /> : 
            <ChevronDown className="h-4 w-4 text-blue-600" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
        )}
      </div>
    </TableHead>
  );

  // Quick action handlers
  const handlePlayAudio = async (call, e) => {
    e.stopPropagation();
    if (playingCallId === call.id) {
      audioRef.current?.pause();
      setPlayingCallId(null);
    } else {
      try {
        if (audioRef.current) {
          // Get authenticated audio URL
          const result = await getCallAudioUrl(call.id);
          if (result.error) {
            toast.error(result.error);
            return;
          }
          audioRef.current.src = result.url;
          audioRef.current.play().catch(() => toast.error('Failed to play audio'));
        }
        setPlayingCallId(call.id);
      } catch (error) {
        console.error('Failed to load audio:', error);
        toast.error(error.response?.status === 404 ? 'No audio file available for this call' : 'Failed to load audio');
      }
    }
  };

  const handleViewTranscript = (call, e) => {
    e.stopPropagation();
    setShowTranscript(call);
  };

  const handleViewFeedback = (call, e) => {
    e.stopPropagation();
    setShowFeedback(call);
  };

  const handleViewScore = (call, e) => {
    e.stopPropagation();
    setShowScore(call);
  };

  const handleRowClick = (call, fromTab = 'calls') => {
    // Support both legacy admin and SaaS tenant routes
    // Pass the source tab so the back button knows where to return
    const baseUrl = adminUrl ? `/admin/${adminUrl}` : '/dashboard';
    const callDetailUrl = `${baseUrl}/call/${call.id}`;
    
    // Determine the back URL and label based on which tab we're on
    let fromUrl = `${baseUrl}/calls`;
    let fromLabel = 'Back to Calls';
    
    if (fromTab === 'unknown') {
      fromUrl = `${baseUrl}/calls?tab=unknown`;
      fromLabel = 'Back to Unknown Calls';
    } else if (fromTab === 'stats') {
      fromUrl = `${baseUrl}/calls?tab=stats`;
      fromLabel = 'Back to Statistics';
    }
    
    navigate(callDetailUrl, { 
      state: { 
        from: fromUrl, 
        fromLabel: fromLabel,
        referrerPath: fromUrl 
      } 
    });
  };
  
  // Generate URL for call detail page (for link href)
  const getCallDetailUrl = (callId) => {
    return adminUrl ? `/admin/${adminUrl}/call/${callId}` : `/dashboard/call/${callId}`;
  };

  // Statistics sorting
  const sortedMonthlyBreakdown = statistics?.monthly_breakdown?.slice().sort((a, b) => {
    if (statsSortField === 'date') {
      return statsSortOrder === 'desc' ? b.month.localeCompare(a.month) : a.month.localeCompare(b.month);
    } else if (statsSortField === 'calls') {
      return statsSortOrder === 'desc' ? b.calls - a.calls : a.calls - b.calls;
    } else if (statsSortField === 'hours') {
      return statsSortOrder === 'desc' ? b.duration_hours - a.duration_hours : a.duration_hours - b.duration_hours;
    }
    return 0;
  }) || [];

  const sortedDailyBreakdown = statistics?.daily_breakdown?.slice().sort((a, b) => {
    if (statsSortField === 'date') {
      return statsSortOrder === 'desc' ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date);
    } else if (statsSortField === 'calls') {
      return statsSortOrder === 'desc' ? b.calls - a.calls : a.calls - b.calls;
    } else if (statsSortField === 'hours') {
      return statsSortOrder === 'desc' ? b.duration_hours - a.duration_hours : a.duration_hours - b.duration_hours;
    }
    return 0;
  }) || [];

  const toggleSort = (field) => {
    if (statsSortField === field) {
      setStatsSortOrder(statsSortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setStatsSortField(field);
      setStatsSortOrder('desc');
    }
  };

  // Handle stats card click - filter by month
  const handleMonthCardClick = (month) => {
    setStatsFilters({ ...statsFilters, month: month.month });
    setFilters({ ...filters, month: month.month });
  };

  // Handle stats card click - navigate to calls tab with filter
  const handleStatCardClick = (filterType, value) => {
    if (filterType === 'user') {
      setFilters({ ...filters, search: value });
    } else if (filterType === 'department') {
      const dept = departments.find(d => d.name === value);
      if (dept) setFilters({ ...filters, department_id: dept.id });
    }
    setActiveTab('calls');
  };

  return (
    <div className="space-y-6" data-testid="admin-calls">
      {/* Header with Upload Button */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Calls</h1>
          <p className="text-muted-foreground mt-1">{total} total calls analyzed</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleCheckDuplicates}
            disabled={checkingDuplicates}
            className="border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
            data-testid="check-duplicates-btn"
          >
            {checkingDuplicates ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Checking...</>
            ) : (
              <><Copy className="h-4 w-4 mr-2" />Clean Duplicates</>
            )}
          </Button>
          <Button onClick={() => setShowUploadDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Upload className="h-4 w-4 mr-2" />
            Upload Calls
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="statistics" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Statistics
          </TabsTrigger>
          <TabsTrigger value="calls" className="gap-2">
            <Phone className="h-4 w-4" />
            Call Management
          </TabsTrigger>
          <TabsTrigger value="unknown" className="gap-2 relative">
            <UserX className="h-4 w-4" />
            Unknown Calls
            {unknownCallsTotal > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 min-w-5 text-xs">
                {unknownCallsTotal}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="deleted" className="gap-2 relative">
            <Trash2 className="h-4 w-4" />
            Deleted Calls
            {deletedCallsTotal > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 text-xs">
                {deletedCallsTotal}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Call Management Tab */}
        <TabsContent value="calls" className="space-y-6 mt-6">
          {/* Compliance Filter Info */}
          {filters.compliance_filter !== 'all' && (
            <div className="bg-amber-500/10 dark:bg-amber-500/20 border border-amber-400 dark:border-amber-600 rounded-lg p-3 flex items-center gap-3">
              <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Filtering by: {complianceFilters.find(f => f.value === filters.compliance_filter)?.label}
                </p>
              </div>
              <Button variant="outline" size="sm" className="ml-auto" onClick={() => setFilters({...filters, compliance_filter: 'all'})}>
                Clear Filter
              </Button>
            </div>
          )}

          {/* Filters */}
          <Card className="card-elevated">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search calls..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    className="pl-10"
                  />
                </div>
                
                <Select value={filters.alert_level} onValueChange={(v) => setFilters({...filters, alert_level: v})}>
                  <SelectTrigger className="w-[160px]"><SelectValue placeholder="Alert Level" /></SelectTrigger>
                  <SelectContent>
                    {ALERT_LEVELS.map(level => (<SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                
                <Select value={filters.classification} onValueChange={(v) => setFilters({...filters, classification: v})}>
                  <SelectTrigger className="w-[180px]"><SelectValue placeholder="Classification" /></SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATIONS.map(cls => (<SelectItem key={cls.value} value={cls.value}>{cls.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                
                <Select value={filters.call_type} onValueChange={(v) => setFilters({...filters, call_type: v})}>
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="Call Type" /></SelectTrigger>
                  <SelectContent>
                    {CALL_TYPES.map(type => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                
                <Select value={filters.department_id} onValueChange={(v) => setFilters({...filters, department_id: v})}>
                  <SelectTrigger className="w-[180px]"><SelectValue placeholder="Department" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (<SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                
                <Select value={filters.source} onValueChange={(v) => setFilters({...filters, source: v})}>
                  <SelectTrigger className="w-[160px]"><SelectValue placeholder="Call Source" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="synced">OneDrive Synced</SelectItem>
                    <SelectItem value="manual_upload">Manual Upload</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Reprocessed Calls Toggle */}
                <Button 
                  variant={filters.is_reprocessed ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setFilters({...filters, is_reprocessed: !filters.is_reprocessed})}
                  className={filters.is_reprocessed ? "bg-purple-600 hover:bg-purple-700" : ""}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${filters.is_reprocessed ? "text-white" : ""}`} />
                  {filters.is_reprocessed ? "Showing Reprocessed" : "View Reprocessed"}
                </Button>
              </div>
              
              {/* Date Filters Row */}
              <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm font-medium">Date Filter:</span>
                </div>
                
                <Select value={filters.month} onValueChange={(v) => setFilters({...filters, month: v, date_from: '', date_to: ''})}>
                  <SelectTrigger className="w-[180px]"><SelectValue placeholder="Select Month" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    {MONTH_OPTIONS.map(month => (<SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>))}
                  </SelectContent>
                </Select>
                
                <span className="text-sm text-muted-foreground">or</span>
                
                <div className="flex items-center gap-2">
                  <Input type="date" value={filters.date_from} onChange={(e) => setFilters({...filters, date_from: e.target.value, month: 'all'})} className="w-[150px]" />
                  <span className="text-muted-foreground">to</span>
                  <Input type="date" value={filters.date_to} onChange={(e) => setFilters({...filters, date_to: e.target.value, month: 'all'})} className="w-[150px]" />
                </div>
                
                {(filters.date_from || filters.date_to || filters.month !== 'all') && (
                  <Button variant="ghost" size="sm" onClick={clearDateFilters} className="text-muted-foreground">
                    <X className="h-4 w-4 mr-1" /> Clear Dates
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Calls Table */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="font-heading flex items-center justify-between">
                <span>All Calls</span>
                <Badge variant="secondary">{total} total</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">{[...Array(5)].map((_, i) => (<Skeleton key={i} className="h-16" />))}</div>
              ) : (
                <>
                <div className="overflow-x-auto">
                {/* Bulk Actions Bar */}
                {selectedCalls.length > 0 && (
                  <div className="flex items-center gap-4 p-3 mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      {selectedCalls.length} call(s) selected
                    </span>
                    <Button 
                      size="sm" 
                      onClick={() => {
                        setMainBulkAction('assign');
                        setMainBulkAssignDialogOpen(true);
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Assign to User
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setMainBulkAction('unknown');
                        setMainBulkAssignDialogOpen(true);
                      }}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30"
                    >
                      <UserX className="h-4 w-4 mr-1" />
                      Mark as Unknown
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={openReprocessDialog}
                      disabled={isReprocessing}
                      className="border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-300 dark:hover:bg-purple-900/30"
                      data-testid="bulk-reprocess-btn"
                    >
                      {isReprocessing ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-1" />
                      )}
                      Reprocess
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleRegenerateTranscript}
                      disabled={isRegeneratingTranscript}
                      className="border-cyan-300 text-cyan-700 hover:bg-cyan-50 dark:border-cyan-700 dark:text-cyan-300 dark:hover:bg-cyan-900/30"
                      data-testid="bulk-regenerate-transcript-btn"
                    >
                      {isRegeneratingTranscript ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <FileSearch className="h-4 w-4 mr-1" />
                      )}
                      Regenerate Transcript
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleRefreshCallTypes}
                      disabled={isRefreshingCallTypes}
                      className="border-cyan-300 text-cyan-700 hover:bg-cyan-50 dark:border-cyan-700 dark:text-cyan-300 dark:hover:bg-cyan-900/30"
                      data-testid="refresh-call-types-btn"
                    >
                      {isRefreshingCallTypes ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-1" />
                      )}
                      Refresh Call Types
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setDeleteDialogOpen(true)}
                      className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30"
                      data-testid="bulk-delete-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => setSelectedCalls([])}
                    >
                      Clear Selection
                    </Button>
                  </div>
                )}
                <Table className="min-w-[1100px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox 
                          checked={calls.length > 0 && selectedCalls.length === calls.length}
                          onCheckedChange={toggleSelectAllCalls}
                        />
                      </TableHead>
                      <SortableHeader field="user" className="min-w-[180px]">User</SortableHeader>
                      <SortableHeader field="call_date" className="min-w-[150px]">Call Date & Time</SortableHeader>
                      <TableHead className="min-w-[100px]">Uploaded</TableHead>
                      <SortableHeader field="call_type" className="min-w-[100px]">Call Type</SortableHeader>
                      <SortableHeader field="duration" className="min-w-[80px]">Duration</SortableHeader>
                      <SortableHeader field="classification" className="w-[100px]">Class</SortableHeader>
                      <SortableHeader field="alert_level" className="w-[80px]">Alert</SortableHeader>
                      <TableHead className="min-w-[100px]">Scoring Guide</TableHead>
                      <TableHead className="min-w-[120px]">Quick Actions</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCalls.map((call) => {
                      // Calculate total alerts (alert_level + compliance_alerts count)
                      const complianceAlertCount = call.compliance_alerts?.length || 0;
                      const hasMultipleAlerts = complianceAlertCount > 0 || (call.alert_level === 'critical' || call.alert_level === 'high_alert');
                      const totalAlerts = complianceAlertCount + (call.alert_level === 'critical' || call.alert_level === 'high_alert' ? 1 : 0);
                      
                      return (
                      <TableRow key={call.id} className={`hover:bg-muted/50 ${call.alert_level === 'critical' ? 'bg-red-50/50 dark:bg-red-900/20' : ''} ${selectedCalls.includes(call.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox 
                            checked={selectedCalls.includes(call.id)}
                            onCheckedChange={() => toggleCallSelection(call.id)}
                          />
                        </TableCell>
                        <TableCell className="p-0">
                          <Link to={getCallDetailUrl(call.id)} className="block p-4 hover:bg-muted/30">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={call.user?.avatar_url} />
                              <AvatarFallback className="bg-muted text-xs">{call.user?.name?.charAt(0) || '?'}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-sm">{call.user?.name || 'Unassigned'}</p>
                              <p className="text-xs text-muted-foreground font-mono">{call.phone_number}</p>
                            </div>
                          </div>
                          </Link>
                        </TableCell>
                        <TableCell className="p-0">
                          <Link to={getCallDetailUrl(call.id)} className="block p-4 hover:bg-muted/30">
                          <div className="text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span className="text-sm">{formatDate(call.call_date)}</span>
                            </div>
                            <span className="text-xs">{formatTime(call.call_date)}</span>
                          </div>
                          </Link>
                        </TableCell>
                        <TableCell className="p-0">
                          <Link to={getCallDetailUrl(call.id)} className="block p-4 hover:bg-muted/30">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs text-muted-foreground">
                              {call.uploaded_at || call.created_at ? formatDate(call.uploaded_at || call.created_at) : '-'}
                            </span>
                            {call.source === 'manual_upload' ? (
                              <Badge variant="outline" className="text-[10px] w-fit px-1.5 py-0 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700">
                                Manual
                              </Badge>
                            ) : call.onedrive_file_id ? (
                              <Badge variant="outline" className="text-[10px] w-fit px-1.5 py-0 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700">
                                Synced
                              </Badge>
                            ) : null}
                          </div>
                          </Link>
                        </TableCell>
                        <TableCell className="p-0">
                          <Link to={getCallDetailUrl(call.id)} className="block p-4 hover:bg-muted/30">
                          <div className="flex items-center gap-1">
                            {/* Call Type Icon with color */}
                            {(() => {
                              const typeInfo = getCallTypeIcon(call.call_type, call.detected_call_type);
                              const TypeIcon = typeInfo.icon;
                              return (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className={`p-1 rounded ${typeInfo.bg}`}>
                                        <TypeIcon className={`h-3.5 w-3.5 ${typeInfo.color}`} />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{typeInfo.label}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            })()}
                            <Badge variant="outline" className="capitalize text-xs">{call.call_type || call.detected_call_type || 'Unknown'}</Badge>
                            {call.is_reprocessed && (
                              <Badge variant="secondary" className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                                RP{call.reprocess_count || 1}
                              </Badge>
                            )}
                            {call.processing_status === 'reprocessing' && (
                              <Badge variant="secondary" className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 animate-pulse">
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Processing
                              </Badge>
                            )}
                            {call.transcript_regeneration_count > 0 && (
                              <Badge variant="secondary" className="text-xs bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300">
                                TR{call.transcript_regeneration_count}
                              </Badge>
                            )}
                          </div>
                          </Link>
                        </TableCell>
                        <TableCell className="p-0">
                          <Link to={getCallDetailUrl(call.id)} className="block p-4 hover:bg-muted/30">
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            <span className="text-sm">{formatDuration(call.duration)}</span>
                          </div>
                          </Link>
                        </TableCell>
                        <TableCell className="p-0">
                          <Link to={getCallDetailUrl(call.id)} className="block p-4 hover:bg-muted/30">
                          <Badge className={`classification-${call.classification} capitalize text-xs`}>
                            {call.classification?.replace('_', ' ')}
                          </Badge>
                          </Link>
                        </TableCell>
                        <TableCell className="p-0">
                          <Link to={getCallDetailUrl(call.id)} className="block p-4 hover:bg-muted/30">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1">
                                  <Badge 
                                    className={`text-xs ${
                                      call.alert_level === 'critical' ? 'bg-red-600 hover:bg-red-700' : 
                                      call.alert_level === 'high_alert' ? 'bg-amber-600 hover:bg-amber-700' : 
                                      'bg-green-600 hover:bg-green-700'
                                    }`}
                                  >
                                    {call.alert_level === 'critical' && <AlertTriangle className="h-3 w-3 mr-1" />}
                                    {call.alert_level === 'critical' ? 'Critical' : 
                                     call.alert_level === 'high_alert' ? 'High' : 
                                     call.alert_level === 'good' ? 'Good' : 
                                     call.alert_level}
                                  </Badge>
                                  {complianceAlertCount > 0 && (
                                    <Badge variant="destructive" className="text-xs px-1.5">
                                      +{complianceAlertCount}
                                    </Badge>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-[300px]">
                                <div className="space-y-1">
                                  <p className="font-medium">Alert: {call.alert_level}</p>
                                  {complianceAlertCount > 0 && (
                                    <div className="text-xs">
                                      <p className="font-medium mb-1">Compliance Alerts ({complianceAlertCount}):</p>
                                      <ul className="list-disc list-inside space-y-0.5">
                                        {call.compliance_alerts?.slice(0, 5).map((alert, i) => (
                                          <li key={i} className="truncate">
                                            {typeof alert === 'string' ? alert : (alert?.type || alert?.description || 'Alert')}
                                          </li>
                                        ))}
                                        {complianceAlertCount > 5 && (
                                          <li className="text-muted-foreground">+{complianceAlertCount - 5} more...</li>
                                        )}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          </Link>
                        </TableCell>
                        <TableCell className="p-0">
                          <Link to={getCallDetailUrl(call.id)} className="block p-4 hover:bg-muted/30">
                            <span className="text-sm text-muted-foreground">
                              {call.scoring_config_name || 'AI'}
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <TooltipProvider>
                            <div className="flex items-center gap-1">
                              <Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => handlePlayAudio(call, e)}>
                                  {playingCallId === call.id ? <Pause className="h-4 w-4 text-blue-600" /> : <Play className="h-4 w-4 text-muted-foreground" />}
                                </Button>
                              </TooltipTrigger><TooltipContent><p>{playingCallId === call.id ? 'Pause' : 'Play'} Audio</p></TooltipContent></Tooltip>
                              <Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => handleViewScore(call, e)}>
                                  <Star className="h-4 w-4 text-amber-500" />
                                </Button>
                              </TooltipTrigger><TooltipContent><p>Score Details</p></TooltipContent></Tooltip>
                              <Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => handleViewTranscript(call, e)}>
                                  <FileText className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              </TooltipTrigger><TooltipContent><p>View Transcript</p></TooltipContent></Tooltip>
                              <Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => handleViewFeedback(call, e)}>
                                  <MessageSquare className="h-4 w-4 text-emerald-600" />
                                </Button>
                              </TooltipTrigger><TooltipContent><p>View Feedback</p></TooltipContent></Tooltip>
                            </div>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell>
                          <Link to={getCallDetailUrl(call.id)}>
                            <Button variant="ghost" size="icon">
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
                </div>
                
                {/* Pagination Controls */}
                {total > ITEMS_PER_PAGE && (
                  <div className="flex items-center justify-between mt-4 px-2">
                    <p className="text-sm text-muted-foreground">
                      Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, total)} of {total} calls
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchCalls(currentPage - 1)}
                        disabled={currentPage <= 1 || isLoading}
                      >
                        Previous
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => fetchCalls(pageNum)}
                              disabled={isLoading}
                              className="w-8 h-8 p-0"
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                        {totalPages > 5 && currentPage < totalPages - 2 && (
                          <>
                            <span className="px-1">...</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => fetchCalls(totalPages)}
                              disabled={isLoading}
                              className="w-8 h-8 p-0"
                            >
                              {totalPages}
                            </Button>
                          </>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchCalls(currentPage + 1)}
                        disabled={currentPage >= totalPages || isLoading}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
                </>
              )}
              
              {!isLoading && filteredCalls.length === 0 && (
                <div className="text-center py-12">
                  <Phone className="h-12 w-12 text-muted-foreground/70 mx-auto mb-4" />
                  <p className="text-muted-foreground">No calls found</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowUploadDialog(true)}>
                    <Upload className="h-4 w-4 mr-2" /> Upload Your First Call
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transcription Statistics Tab */}
        <TabsContent value="statistics" className="space-y-6 mt-6">
          {/* Statistics Filters */}
          <Card className="card-elevated">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={statsFilters.search}
                    onChange={(e) => setStatsFilters({...statsFilters, search: e.target.value})}
                    className="pl-10"
                  />
                </div>
                
                <Select value={statsFilters.user_id} onValueChange={(v) => setStatsFilters({...statsFilters, user_id: v})}>
                  <SelectTrigger className="w-[200px]">
                    <Users className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Select User" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map(user => (<SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                
                <Select value={statsFilters.department_id} onValueChange={(v) => setStatsFilters({...statsFilters, department_id: v})}>
                  <SelectTrigger className="w-[200px]">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Select Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (<SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                
                <Select value={statsFilters.month} onValueChange={(v) => setStatsFilters({...statsFilters, month: v})}>
                  <SelectTrigger className="w-[180px]">
                    <Calendar className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Select Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    {MONTH_OPTIONS.map(month => (<SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>))}
                  </SelectContent>
                </Select>

                {(statsFilters.user_id !== 'all' || statsFilters.department_id !== 'all' || statsFilters.month !== 'all') && (
                  <Button variant="ghost" size="sm" onClick={() => setStatsFilters({ search: '', user_id: 'all', department_id: 'all', month: 'all', date_from: '', date_to: '' })} className="text-muted-foreground">
                    <X className="h-4 w-4 mr-1" /> Clear All Filters
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Statistics Content */}
          {statsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (<Skeleton key={i} className="h-32" />))}
            </div>
          ) : statistics ? (
            <>
              {/* Summary Cards - Clickable */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="card-elevated hover:shadow-lg transition-shadow cursor-pointer border-blue-400/50 dark:border-blue-500/30 hover:border-blue-500" onClick={() => setActiveTab('calls')}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-500/20 dark:bg-blue-500/30 rounded-lg">
                        <Phone className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <span className="text-sm text-muted-foreground font-medium">Total Calls</span>
                    </div>
                    <p className="text-4xl font-bold text-foreground">{statistics.total_calls}</p>
                    <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">Click to view all calls →</p>
                  </CardContent>
                </Card>
                
                <Card className="card-elevated hover:shadow-lg transition-shadow cursor-pointer border-indigo-400/50 dark:border-indigo-500/30 hover:border-indigo-500">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-indigo-500/20 dark:bg-indigo-500/30 rounded-lg">
                        <Headphones className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <span className="text-sm text-muted-foreground font-medium">Total Hours</span>
                    </div>
                    <p className="text-4xl font-bold text-foreground">{statistics.total_hours.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{statistics.total_minutes.toFixed(0)} minutes transcribed</p>
                  </CardContent>
                </Card>
                
                <Card className="card-elevated hover:shadow-lg transition-shadow cursor-pointer border-purple-400/50 dark:border-purple-500/30 hover:border-purple-500">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-purple-500/20 dark:bg-purple-500/30 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <span className="text-sm text-muted-foreground font-medium">Avg Duration</span>
                    </div>
                    <p className="text-4xl font-bold text-foreground">{statistics.average_duration_minutes.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground mt-1">minutes per call</p>
                  </CardContent>
                </Card>
                
                <Card className="card-elevated hover:shadow-lg transition-shadow cursor-pointer border-emerald-400/50 dark:border-emerald-500/30 hover:border-emerald-500">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-emerald-500/20 dark:bg-emerald-500/30 rounded-lg">
                        <Calendar className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <span className="text-sm text-muted-foreground font-medium">Active Months</span>
                    </div>
                    <p className="text-4xl font-bold text-foreground">{statistics.monthly_breakdown?.length || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">months with activity</p>
                  </CardContent>
                </Card>
              </div>

              {/* Sorting Controls */}
              <div className="flex items-center gap-4 bg-muted/50 rounded-lg p-3">
                <span className="text-sm font-medium text-muted-foreground">Sort by:</span>
                <Button variant={statsSortField === 'date' ? 'secondary' : 'ghost'} size="sm" onClick={() => toggleSort('date')} className="gap-1">
                  Date {statsSortField === 'date' && (statsSortOrder === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                </Button>
                <Button variant={statsSortField === 'calls' ? 'secondary' : 'ghost'} size="sm" onClick={() => toggleSort('calls')} className="gap-1">
                  Calls {statsSortField === 'calls' && (statsSortOrder === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                </Button>
                <Button variant={statsSortField === 'hours' ? 'secondary' : 'ghost'} size="sm" onClick={() => toggleSort('hours')} className="gap-1">
                  Hours {statsSortField === 'hours' && (statsSortOrder === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
                </Button>
              </div>

              {/* Monthly & Daily Tables */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Breakdown Table */}
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-500" />
                      Monthly Breakdown
                    </CardTitle>
                    <CardDescription>Click on a month to filter calls</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>
                          <TableHead className="text-right">Calls</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Avg (min)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedMonthlyBreakdown.length > 0 ? sortedMonthlyBreakdown.map((month) => (
                          <TableRow 
                            key={month.month} 
                            className="cursor-pointer hover:bg-blue-50 transition-colors"
                            onClick={() => handleMonthCardClick(month)}
                          >
                            <TableCell className="font-medium">{month.label}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="secondary">{month.calls}</Badge>
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">{month.duration_hours.toFixed(2)}h</TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {month.calls > 0 ? ((month.duration_seconds / month.calls) / 60).toFixed(1) : '0'}
                            </TableCell>
                          </TableRow>
                        )) : (
                          <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No data available</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Daily Breakdown Table */}
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="h-5 w-5 text-indigo-500" />
                      Daily Activity
                    </CardTitle>
                    <CardDescription>Recent transcription activity by day</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[400px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Day</TableHead>
                            <TableHead className="text-right">Calls</TableHead>
                            <TableHead className="text-right">Hours</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortedDailyBreakdown.length > 0 ? sortedDailyBreakdown.map((day) => (
                            <TableRow key={day.date} className="hover:bg-indigo-50 transition-colors">
                              <TableCell className="font-medium">{day.label}</TableCell>
                              <TableCell className="text-muted-foreground">{day.day_of_week}</TableCell>
                              <TableCell className="text-right">
                                <Badge variant="outline">{day.calls}</Badge>
                              </TableCell>
                              <TableCell className="text-right text-muted-foreground">{day.duration_hours.toFixed(2)}h</TableCell>
                            </TableRow>
                          )) : (
                            <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No data available</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <BarChart3 className="h-12 w-12 text-muted-foreground/70 mx-auto mb-4" />
              <p className="text-muted-foreground">No statistics available</p>
            </div>
          )}
        </TabsContent>

        {/* Unknown Calls Tab */}
        <TabsContent value="unknown" className="space-y-6 mt-6">
          {/* Unknown Calls Info Banner */}
          <div className="bg-amber-500/10 dark:bg-amber-500/20 border border-amber-400 dark:border-amber-600 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <HelpCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-amber-800 dark:text-amber-300">Unknown Calls</h3>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  These calls couldn't be matched to any user in the system based on phone number. 
                  They are still transcribed and scored. Once you create users with matching phone numbers, 
                  click "Sync Calls" to automatically assign them.
                </p>
              </div>
            </div>
          </div>

          {/* Stats & Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600">{unknownCallsStats?.total_unknown_calls || 0}</p>
                <p className="text-sm text-muted-foreground">Unknown Calls</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-amber-600">{unknownCallsStats?.unique_phone_numbers || 0}</p>
                <p className="text-sm text-muted-foreground">Unique Numbers</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchUnknownCalls} disabled={isLoadingUnknown}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingUnknown ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={handleSyncCalls} disabled={isSyncing || unknownCallsTotal === 0}>
                {isSyncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4 mr-2" />
                )}
                Sync Calls with Users
              </Button>
            </div>
          </div>

          {/* Phone Number Breakdown */}
          {unknownCallsStats?.phone_number_breakdown?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Phone Number Summary</CardTitle>
                <CardDescription>Breakdown of unknown calls by phone number</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {unknownCallsStats.phone_number_breakdown.slice(0, 12).map((stat, idx) => (
                    <div key={idx} className="p-3 bg-muted/50 rounded-lg text-center">
                      <p className="font-mono text-sm font-medium">{stat.phone_number || 'unknown'}</p>
                      <p className="text-xs text-muted-foreground">{stat.count} calls</p>
                      <Badge variant="outline" className="mt-1 text-xs">
                        Avg: {stat.average_score}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Unknown Calls Table */}
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <UserX className="h-5 w-5 text-red-500" />
                    Unknown Calls ({unknownCallsTotal})
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowUnknownFilters(!showUnknownFilters)}
                      className={showUnknownFilters ? 'bg-primary/10' : ''}
                    >
                      <Filter className="h-4 w-4 mr-2" />
                      Filters
                      {Object.values(unknownFilters).some(v => v && v !== 'all') && (
                        <Badge variant="secondary" className="ml-2">{Object.values(unknownFilters).filter(v => v && v !== 'all').length}</Badge>
                      )}
                    </Button>
                    {selectedUnknownCalls.length > 0 && (
                      <>
                        <Badge variant="secondary">{selectedUnknownCalls.length} selected</Badge>
                        <Button 
                          size="sm" 
                          onClick={() => setBulkAssignDialogOpen(true)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Assign Selected
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setSelectedUnknownCalls([])}
                        >
                          Clear Selection
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Filter Panel */}
                {showUnknownFilters && (
                  <div className="p-4 bg-muted/50 rounded-lg border space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Search */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Search</Label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Phone number, filename..."
                            value={unknownFilters.search}
                            onChange={(e) => handleUnknownFilterChange('search', e.target.value)}
                            className="pl-9"
                          />
                        </div>
                      </div>
                      
                      {/* Call Type */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Call Type</Label>
                        <Select value={unknownFilters.callType} onValueChange={(v) => handleUnknownFilterChange('callType', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="All Types" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="inbound">Inbound</SelectItem>
                            <SelectItem value="outbound">Outbound</SelectItem>
                            <SelectItem value="unknown">Unknown</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Classification */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Classification</Label>
                        <Select value={unknownFilters.classification} onValueChange={(v) => handleUnknownFilterChange('classification', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="All Classifications" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Classifications</SelectItem>
                            <SelectItem value="best">Best</SelectItem>
                            <SelectItem value="good">Good</SelectItem>
                            <SelectItem value="average">Average</SelectItem>
                            <SelectItem value="needs_improvement">Needs Improvement</SelectItem>
                            <SelectItem value="high_alert">High Alert</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Date From */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Date From</Label>
                        <Input
                          type="date"
                          value={unknownFilters.dateFrom}
                          onChange={(e) => handleUnknownFilterChange('dateFrom', e.target.value)}
                        />
                      </div>
                      
                      {/* Date To */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Date To</Label>
                        <Input
                          type="date"
                          value={unknownFilters.dateTo}
                          onChange={(e) => handleUnknownFilterChange('dateTo', e.target.value)}
                        />
                      </div>
                      
                      {/* Min Score */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Min Score</Label>
                        <Input
                          type="number"
                          min="0"
                          max="10"
                          step="0.1"
                          placeholder="0"
                          value={unknownFilters.minScore}
                          onChange={(e) => handleUnknownFilterChange('minScore', e.target.value)}
                        />
                      </div>
                      
                      {/* Max Score */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Max Score</Label>
                        <Input
                          type="number"
                          min="0"
                          max="10"
                          step="0.1"
                          placeholder="10"
                          value={unknownFilters.maxScore}
                          onChange={(e) => handleUnknownFilterChange('maxScore', e.target.value)}
                        />
                      </div>
                      
                      {/* Clear Button */}
                      <div className="flex items-end">
                        <Button variant="outline" onClick={clearUnknownFilters} className="w-full">
                          <X className="h-4 w-4 mr-2" />
                          Clear Filters
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingUnknown ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}
                </div>
              ) : unknownCalls.length > 0 ? (
                <>
                <div className="overflow-x-auto">
                  <Table className="min-w-[900px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50px]">
                          <Checkbox
                            checked={selectedUnknownCalls.length === unknownCalls.length && unknownCalls.length > 0}
                            onCheckedChange={toggleSelectAllUnknownCalls}
                          />
                        </TableHead>
                        <TableHead 
                          className="min-w-[120px] cursor-pointer hover:bg-muted/50"
                          onClick={() => handleUnknownSort('phone_number')}
                        >
                          <div className="flex items-center gap-1">
                            Phone Number
                            {unknownSortField === 'phone_number' && (
                              unknownSortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                            )}
                            {unknownSortField !== 'phone_number' && <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="min-w-[100px] cursor-pointer hover:bg-muted/50"
                          onClick={() => handleUnknownSort('call_date')}
                        >
                          <div className="flex items-center gap-1">
                            Date
                            {unknownSortField === 'call_date' && (
                              unknownSortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                            )}
                            {unknownSortField !== 'call_date' && <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-[80px]">Time</TableHead>
                        <TableHead 
                          className="min-w-[100px] cursor-pointer hover:bg-muted/50"
                          onClick={() => handleUnknownSort('call_type')}
                        >
                          <div className="flex items-center gap-1">
                            Call Type
                            {unknownSortField === 'call_type' && (
                              unknownSortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                            )}
                            {unknownSortField !== 'call_type' && <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="min-w-[80px] cursor-pointer hover:bg-muted/50"
                          onClick={() => handleUnknownSort('duration')}
                        >
                          <div className="flex items-center gap-1">
                            Duration
                            {unknownSortField === 'duration' && (
                              unknownSortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                            )}
                            {unknownSortField !== 'duration' && <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="min-w-[70px] cursor-pointer hover:bg-muted/50"
                          onClick={() => handleUnknownSort('average_score')}
                        >
                          <div className="flex items-center gap-1">
                            Score
                            {unknownSortField === 'average_score' && (
                              unknownSortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                            )}
                            {unknownSortField !== 'average_score' && <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="min-w-[100px] cursor-pointer hover:bg-muted/50"
                          onClick={() => handleUnknownSort('classification')}
                        >
                          <div className="flex items-center gap-1">
                            Classification
                            {unknownSortField === 'classification' && (
                              unknownSortOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                            )}
                            {unknownSortField !== 'classification' && <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-[150px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {unknownCalls.map((call) => (
                        <TableRow 
                          key={call.id} 
                          className={cn(
                            "hover:bg-muted/50 cursor-pointer",
                            selectedUnknownCalls.includes(call.id) && "bg-blue-50 dark:bg-blue-900/20"
                          )}
                          onClick={() => handleRowClick(call, 'unknown')}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedUnknownCalls.includes(call.id)}
                              onCheckedChange={() => toggleUnknownCallSelection(call.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <UserX className="h-4 w-4 text-muted-foreground" />
                              <span className="font-mono text-sm">{call.phone_number || 'unknown'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{formatDate(call.call_date)}</TableCell>
                          <TableCell className="text-muted-foreground">{formatTime(call.call_date)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {/* Call Type Icon with color */}
                              {(() => {
                                const typeInfo = getCallTypeIcon(call.call_type, call.detected_call_type);
                                const TypeIcon = typeInfo.icon;
                                return (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className={`p-1 rounded ${typeInfo.bg}`}>
                                          <TypeIcon className={`h-3.5 w-3.5 ${typeInfo.color}`} />
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{typeInfo.label}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                );
                              })()}
                              <Badge variant="outline" className="capitalize">
                                {call.call_type?.replace('_', ' ') || call.detected_call_type?.replace('_', ' ') || 'Unknown'}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{formatDuration(call.duration)}</TableCell>
                          <TableCell>
                            <Badge className={
                              call.average_score >= 7 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                              call.average_score >= 5 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                              'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            }>
                              {call.average_score?.toFixed(1) || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              call.classification === 'best' ? 'default' :
                              call.classification === 'good' ? 'secondary' :
                              call.classification === 'high_alert' ? 'destructive' : 'outline'
                            } className="capitalize">
                              {call.classification?.replace('_', ' ') || 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={(e) => { e.stopPropagation(); handlePlayAudio(call, e); }}
                                    >
                                      {playingCallId === call.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Play Audio</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={(e) => { e.stopPropagation(); setShowTranscript(call); }}
                                    >
                                      <FileText className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>View Transcript</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-8"
                                      onClick={() => {
                                        setCallToAssign(call);
                                        setAssignDialogOpen(true);
                                      }}
                                    >
                                      <UserPlus className="h-4 w-4 mr-1" />
                                      Assign
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Assign to User</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Unknown Calls Pagination */}
                {unknownCallsTotal > ITEMS_PER_PAGE && (
                  <div className="flex items-center justify-between mt-4 px-2">
                    <p className="text-sm text-muted-foreground">
                      Showing {((unknownCurrentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(unknownCurrentPage * ITEMS_PER_PAGE, unknownCallsTotal)} of {unknownCallsTotal} calls
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchUnknownCalls(unknownCurrentPage - 1)}
                        disabled={unknownCurrentPage <= 1 || isLoadingUnknown}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        Page {unknownCurrentPage} of {unknownTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchUnknownCalls(unknownCurrentPage + 1)}
                        disabled={unknownCurrentPage >= unknownTotalPages || isLoadingUnknown}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
                </>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-4" />
                  <p className="text-muted-foreground font-medium">No Unknown Calls!</p>
                  <p className="text-sm text-muted-foreground mt-1">All calls are assigned to users</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Deleted Calls Tab */}
        <TabsContent value="deleted" className="space-y-6 mt-6">
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-foreground">Deleted Calls</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    View and manage calls that have been soft-deleted
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {selectedDeletedCalls.length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRestoreCalls}
                        disabled={isRestoring}
                        className="gap-2"
                      >
                        {isRestoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Restore ({selectedDeletedCalls.length})
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handlePermanentDelete}
                        className="gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Permanently Delete
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search deleted calls..."
                    value={deletedSearch}
                    onChange={(e) => setDeletedSearch(e.target.value)}
                    className="pl-10 bg-background border-input"
                  />
                </div>
              </div>

              {/* Archived Data Stats Banner */}
              {archivedStats && archivedStats.total_archived_records > 0 && (
                <div className="mb-4 p-4 bg-amber-500/10 dark:bg-amber-500/20 border border-amber-500/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Archive className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <span className="font-medium text-amber-700 dark:text-amber-300">Archived Related Data</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    When calls are deleted, their related data is archived. Restoring a call also restores its related data.
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs">
                    {archivedStats.stats.archived_qa_notes > 0 && (
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-700 dark:text-purple-300 rounded">
                        QA Notes: {archivedStats.stats.archived_qa_notes}
                      </span>
                    )}
                    {archivedStats.stats.archived_qa_alerts > 0 && (
                      <span className="px-2 py-1 bg-red-500/20 text-red-700 dark:text-red-300 rounded">
                        QA Alerts: {archivedStats.stats.archived_qa_alerts}
                      </span>
                    )}
                    {archivedStats.stats.archived_review_requests > 0 && (
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-700 dark:text-blue-300 rounded">
                        Review Requests: {archivedStats.stats.archived_review_requests}
                      </span>
                    )}
                    {archivedStats.stats.archived_call_audit_trail > 0 && (
                      <span className="px-2 py-1 bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 rounded">
                        Audit Trail: {archivedStats.stats.archived_call_audit_trail}
                      </span>
                    )}
                    {archivedStats.stats.archived_qa_activities > 0 && (
                      <span className="px-2 py-1 bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 rounded">
                        QA Activities: {archivedStats.stats.archived_qa_activities}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {isLoadingDeleted ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : deletedCalls.length > 0 ? (
                <>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-muted/50">
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={selectedDeletedCalls.length === deletedCalls.length && deletedCalls.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedDeletedCalls(deletedCalls.map(c => c.id));
                            } else {
                              setSelectedDeletedCalls([]);
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead className="text-muted-foreground">Filename</TableHead>
                      <TableHead className="text-muted-foreground">User</TableHead>
                      <TableHead className="text-muted-foreground">Call Date</TableHead>
                      <TableHead className="text-muted-foreground">Deleted At</TableHead>
                      <TableHead className="text-muted-foreground">Archived Data</TableHead>
                      <TableHead className="text-muted-foreground">Deletion Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deletedCalls.map((call) => (
                      <TableRow key={call.id} className="border-border hover:bg-muted/50">
                        <TableCell>
                          <Checkbox
                            checked={selectedDeletedCalls.includes(call.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedDeletedCalls(prev => [...prev, call.id]);
                              } else {
                                setSelectedDeletedCalls(prev => prev.filter(id => id !== call.id));
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell className="text-foreground font-medium">
                          <div className="flex items-center gap-2">
                            <FileAudio className="h-4 w-4 text-muted-foreground" />
                            {call.filename}
                          </div>
                        </TableCell>
                        <TableCell className="text-foreground">
                          {call.user_name || call.user_id || 'Unknown'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {call.call_date ? new Date(call.call_date).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {call.deleted_at ? new Date(call.deleted_at).toLocaleString() : '-'}
                        </TableCell>
                        <TableCell>
                          {call.archived_related_data ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1">
                                    <Archive className="h-3 w-3 text-amber-500" />
                                    <span className="text-xs text-amber-600 dark:text-amber-400">
                                      {Object.values(call.archived_related_data).reduce((a, b) => a + b, 0)} records
                                    </span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="text-xs space-y-1">
                                    {call.archived_related_data.qa_notes > 0 && <div>QA Notes: {call.archived_related_data.qa_notes}</div>}
                                    {call.archived_related_data.qa_alerts > 0 && <div>QA Alerts: {call.archived_related_data.qa_alerts}</div>}
                                    {call.archived_related_data.review_requests > 0 && <div>Review Requests: {call.archived_related_data.review_requests}</div>}
                                    {call.archived_related_data.call_audit_trail > 0 && <div>Audit Trail: {call.archived_related_data.call_audit_trail}</div>}
                                    {call.archived_related_data.notifications > 0 && <div>Notifications: {call.archived_related_data.notifications}</div>}
                                    {call.archived_related_data.qa_activities > 0 && <div>QA Activities: {call.archived_related_data.qa_activities}</div>}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground max-w-xs truncate">
                          {call.deletion_reason || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                {deletedTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Page {deletedCurrentPage} of {deletedTotalPages} ({deletedCallsTotal} total)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={deletedCurrentPage <= 1}
                        onClick={() => fetchDeletedCalls(deletedCurrentPage - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={deletedCurrentPage >= deletedTotalPages}
                        onClick={() => fetchDeletedCalls(deletedCurrentPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
                </>
              ) : (
                <div className="text-center py-12">
                  <Trash2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground font-medium">No Deleted Calls</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Deleted calls will appear here for review and restoration
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign Call Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Assign Call to User
            </DialogTitle>
            <DialogDescription>
              Manually assign this call to a user. The call's phone number is: {callToAssign?.phone_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select User</Label>
              <Select value={assignUserId} onValueChange={setAssignUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user to assign" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={user.avatar_url} />
                          <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <span>{user.name}</span>
                        {user.extension && <span className="text-muted-foreground text-sm">(Ext: {user.extension})</span>}
                        {user.phone_number && <span className="text-muted-foreground text-sm">({user.phone_number})</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {callToAssign && (
              <div className="bg-muted/50 p-3 rounded-lg text-sm">
                <p><strong>Filename:</strong> {callToAssign.filename}</p>
                <p><strong>Date:</strong> {formatDate(callToAssign.call_date)} {formatTime(callToAssign.call_date)}</p>
                <p><strong>Score:</strong> {callToAssign.average_score?.toFixed(1) || 'N/A'}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignCall} disabled={!assignUserId}>Assign Call</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignDialogOpen} onOpenChange={setBulkAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Bulk Assign Calls to User
            </DialogTitle>
            <DialogDescription>
              Assign {selectedUnknownCalls.length} selected call(s) to a single user
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select User</Label>
              <Select value={bulkAssignUserId} onValueChange={setBulkAssignUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user to assign all calls" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={user.avatar_url} />
                          <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <span>{user.name}</span>
                        {user.extension && <span className="text-muted-foreground text-sm">(Ext: {user.extension})</span>}
                        {user.phone_number && <span className="text-muted-foreground text-sm">({user.phone_number})</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-amber-500/10 dark:bg-amber-500/20 border border-amber-400 dark:border-amber-600 p-3 rounded-lg text-sm">
              <p className="font-medium text-amber-800 dark:text-amber-300 mb-1">Selected Calls ({selectedUnknownCalls.length}):</p>
              <ul className="text-amber-700 dark:text-amber-400 max-h-32 overflow-y-auto space-y-1">
                {unknownCalls.filter(c => selectedUnknownCalls.includes(c.id)).slice(0, 5).map(call => (
                  <li key={call.id} className="flex items-center gap-2">
                    <span className="font-mono">{call.phone_number || 'unknown'}</span>
                    <span className="text-xs">- {formatDate(call.call_date)}</span>
                  </li>
                ))}
                {selectedUnknownCalls.length > 5 && (
                  <li className="text-xs text-amber-600 dark:text-amber-500">...and {selectedUnknownCalls.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkAssignCalls} 
              disabled={!bulkAssignUserId || isBulkAssigning}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isBulkAssigning ? 'Assigning...' : `Assign ${selectedUnknownCalls.length} Call(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Calls Bulk Assign Dialog */}
      <Dialog open={mainBulkAssignDialogOpen} onOpenChange={setMainBulkAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mainBulkAction === 'unknown' ? (
                <><UserX className="h-5 w-5 text-amber-600" /> Mark Calls as Unknown</>
              ) : (
                <><Users className="h-5 w-5 text-blue-600" /> Bulk Assign Calls</>
              )}
            </DialogTitle>
            <DialogDescription>
              {mainBulkAction === 'unknown' 
                ? `Mark ${selectedCalls.length} selected call(s) as unknown/unassigned`
                : `Assign ${selectedCalls.length} selected call(s) to a user`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {mainBulkAction === 'assign' && (
              <div className="space-y-2">
                <Label>Select User</Label>
                <Select value={mainBulkAssignUserId} onValueChange={setMainBulkAssignUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a user to assign" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={user.avatar_url} />
                            <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span>{user.name}</span>
                          {user.extension && <span className="text-muted-foreground text-sm">(Ext: {user.extension})</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="bg-muted/50 p-3 rounded-lg">
              <Label className="text-sm font-medium mb-2 block">Selected Calls ({selectedCalls.length})</Label>
              <ul className="space-y-1 max-h-40 overflow-y-auto text-sm">
                {selectedCalls.slice(0, 5).map(callId => {
                  const call = calls.find(c => c.id === callId);
                  return (
                    <li key={callId} className="flex items-center gap-2 text-muted-foreground">
                      <span className="font-mono text-xs">{call?.filename || callId}</span>
                      <span className="text-xs">- {call?.user?.name || 'Unassigned'}</span>
                    </li>
                  );
                })}
                {selectedCalls.length > 5 && (
                  <li className="text-xs text-muted-foreground">...and {selectedCalls.length - 5} more</li>
                )}
              </ul>
            </div>
            {mainBulkAction === 'unknown' && (
              <div className="p-3 bg-amber-500/10 dark:bg-amber-500/20 border border-amber-400 dark:border-amber-600 rounded-lg text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                This will remove user assignments and move calls to the Unknown Calls tab.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMainBulkAssignDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleMainBulkAssign} 
              disabled={(mainBulkAction === 'assign' && !mainBulkAssignUserId) || isMainBulkAssigning}
              className={mainBulkAction === 'unknown' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}
            >
              {isMainBulkAssigning ? 'Processing...' : mainBulkAction === 'unknown' ? `Mark ${selectedCalls.length} as Unknown` : `Assign ${selectedCalls.length} Call(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Call Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => !isUploading && (open ? setShowUploadDialog(true) : resetUploadDialog())}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5" /> Upload Call Recordings</DialogTitle>
            <DialogDescription>Upload audio files for AI analysis. Supported: MP3, WAV, M4A, OGG, FLAC, WMA, AAC</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${uploadFiles.length > 0 ? 'border-blue-300 bg-blue-50' : 'border-border hover:border-slate-400'}`} onClick={() => fileInputRef.current?.click()} style={{ cursor: 'pointer' }}>
              <input ref={fileInputRef} type="file" multiple accept=".mp3,.wav,.m4a,.ogg,.flac,.wma,.aac" onChange={handleFileSelect} className="hidden" />
              <FileAudio className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground">Click to select audio files</p>
              <p className="text-xs text-muted-foreground mt-1">or drag and drop files here</p>
            </div>
            {uploadFiles.length > 0 && (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                <Label className="text-sm font-medium">Selected Files ({uploadFiles.length})</Label>
                {uploadFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                    <FileAudio className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    {uploadProgress[idx]?.status === 'uploading' && <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />}
                    {uploadProgress[idx]?.status === 'complete' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                    {uploadProgress[idx]?.status === 'duplicate' && (
                      <span className="flex items-center gap-1 text-amber-600 text-xs">
                        <Copy className="h-4 w-4" />
                        <span>Duplicate</span>
                      </span>
                    )}
                    {uploadProgress[idx]?.status === 'error' && <AlertTriangle className="h-4 w-4 text-red-600" />}
                    {!isUploading && <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); removeFile(idx); }}><X className="h-3 w-3" /></Button>}
                  </div>
                ))}
              </div>
            )}
            <div>
              <Label className="text-sm font-medium">Assign to User (Optional)</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select user or leave unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map(user => (<SelectItem key={user.id} value={user.id}>{user.name} - {user.department?.name || 'No Dept'}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetUploadDialog} disabled={isUploading}>Cancel</Button>
            <Button onClick={handleUploadCalls} disabled={uploadFiles.length === 0 || isUploading}>
              {isUploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4 mr-2" /> Upload {uploadFiles.length} File{uploadFiles.length !== 1 ? 's' : ''}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden Audio Element */}
      <audio ref={audioRef} onEnded={() => setPlayingCallId(null)} className="hidden" />

      {/* Quick View Score Dialog */}
      <Dialog open={!!showScore} onOpenChange={() => setShowScore(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Star className="h-5 w-5 text-amber-500" /> Score Details</DialogTitle>
            <DialogDescription>{showScore && `${showScore.call_type} call - ${formatDate(showScore.call_date)}`}</DialogDescription>
          </DialogHeader>
          {showScore && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <p className="text-4xl font-bold text-foreground">{showScore.average_score}</p>
                <p className="text-sm text-muted-foreground">Overall Score</p>
                <Badge className={`classification-${showScore.classification} capitalize mt-2`}>{showScore.classification?.replace('_', ' ')}</Badge>
              </div>
              {showScore.score_breakdown && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Score Breakdown</p>
                  {Object.entries(showScore.score_breakdown).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-semibold">{value}/10</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate Cleanup Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-amber-500" />
              Duplicate Calls Cleanup
            </DialogTitle>
            <DialogDescription>
              Find and remove duplicate calls based on filename pattern (date + phone number). The system keeps the call with more compliance alerts (conservative approach).
            </DialogDescription>
          </DialogHeader>
          
          {duplicateData ? (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                  <p className="text-sm text-muted-foreground">Duplicate Groups</p>
                  <p className="text-2xl font-bold text-amber-600">{duplicateData.duplicate_groups}</p>
                </div>
                <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                  <p className="text-sm text-muted-foreground">Calls to Remove</p>
                  <p className="text-2xl font-bold text-red-600">{duplicateData.calls_to_remove}</p>
                </div>
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <p className="text-sm text-muted-foreground">Calls to Keep</p>
                  <p className="text-2xl font-bold text-green-600">
                    {duplicateData.duplicate_groups}
                  </p>
                </div>
              </div>
              
              {duplicateData.duplicate_groups === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-foreground font-medium">No duplicates found!</p>
                  <p className="text-sm text-muted-foreground">All calls have unique filenames</p>
                </div>
              ) : (
                <>
                  {/* Duplicate List Preview */}
                  <div className="max-h-64 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="p-2 text-left">Filename</th>
                          <th className="p-2 text-center">Duplicates</th>
                          <th className="p-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {duplicateData.duplicates?.slice(0, 20).map((dup, idx) => (
                          <tr key={idx} className="border-t hover:bg-muted/50">
                            <td className="p-2 font-mono text-xs truncate max-w-[200px]" title={dup._id}>
                              {dup._id}
                            </td>
                            <td className="p-2 text-center">
                              <Badge variant="destructive">{dup.count}</Badge>
                            </td>
                            <td className="p-2 text-right text-muted-foreground text-xs">
                              Keep best, remove {dup.count - 1}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {duplicateData.duplicates?.length > 20 && (
                      <p className="p-2 text-center text-muted-foreground text-xs">
                        +{duplicateData.duplicates.length - 20} more groups...
                      </p>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Keeps calls with more compliance alerts (conservative approach)
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>
                        Cancel
                      </Button>
                      <Button 
                        variant="destructive"
                        onClick={() => handleCleanupDuplicates(false)}
                        disabled={cleaningDuplicates}
                        data-testid="remove-duplicates-btn"
                      >
                        {cleaningDuplicates ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Cleaning...</>
                        ) : (
                          <><Trash2 className="h-4 w-4 mr-2" />Remove {duplicateData.calls_to_remove} Duplicates</>
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick View Transcript Dialog */}
      <Dialog open={!!showTranscript} onOpenChange={() => { setShowTranscript(null); setShowTranscriptHistory(false); setSelectedHistoryIndex(0); }}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Call Transcript</DialogTitle>
            <DialogDescription>{showTranscript && `${showTranscript.call_type} call - ${formatDate(showTranscript.call_date)}`}</DialogDescription>
          </DialogHeader>
          {showTranscript && (
            <div className="space-y-4">
              {/* Transcript History Toggle */}
              {showTranscript.transcript_history?.length > 0 && (
                <div className="flex items-center justify-between p-3 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileSearch className="h-4 w-4 text-cyan-600" />
                    <span className="text-sm text-cyan-700 dark:text-cyan-300">
                      {showTranscript.transcript_history.length} previous version(s) available
                    </span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowTranscriptHistory(!showTranscriptHistory)}
                    className="text-xs"
                  >
                    {showTranscriptHistory ? 'Hide History' : 'View History'}
                  </Button>
                </div>
              )}
              
              {/* History Selector */}
              {showTranscriptHistory && showTranscript.transcript_history?.length > 0 && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Select Version:</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={selectedHistoryIndex === -1 ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedHistoryIndex(-1)}
                      className="text-xs"
                    >
                      Current (Latest)
                    </Button>
                    {showTranscript.transcript_history.map((hist, idx) => (
                      <Button
                        key={idx}
                        variant={selectedHistoryIndex === idx ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedHistoryIndex(idx)}
                        className="text-xs"
                      >
                        Version {showTranscript.transcript_history.length - idx} 
                        <span className="ml-1 text-muted-foreground">
                          ({hist.saved_at ? new Date(hist.saved_at).toLocaleDateString() : 'N/A'})
                        </span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Main Transcript */}
              <div className="overflow-y-auto max-h-96 p-4 bg-muted/50 rounded-lg space-y-3">
                {(() => {
                  // Get the transcript to display
                  const displayTranscript = showTranscriptHistory && selectedHistoryIndex >= 0 
                    ? showTranscript.transcript_history[selectedHistoryIndex]?.transcript 
                    : showTranscript.transcript;
                  
                  if (!displayTranscript) {
                    return <p className="text-sm text-muted-foreground text-center py-4">No transcript available.</p>;
                  }
                  
                  if (Array.isArray(displayTranscript)) {
                    return displayTranscript.map((segment, idx) => {
                      const speaker = segment.speaker?.toLowerCase() || '';
                      const isRecruiter = speaker.includes('recruiter') || segment.speaker?.includes('1');
                      const isSystem = speaker.includes('system') || speaker.includes('generated');
                      const isCandidate = speaker.includes('candidate') || speaker.includes('2');
                      
                      let bgClass, borderClass, textClass;
                      if (isSystem) {
                        bgClass = 'bg-muted/50 dark:bg-slate-700/50';
                        borderClass = 'border-slate-400';
                        textClass = 'text-muted-foreground dark:text-muted-foreground';
                      } else if (isRecruiter || (!isCandidate && !isSystem && idx % 2 === 0)) {
                        bgClass = 'bg-blue-100/50 dark:bg-blue-900/20';
                        borderClass = 'border-blue-400';
                        textClass = 'text-blue-700 dark:text-blue-300';
                      } else {
                        bgClass = 'bg-green-100/50 dark:bg-green-900/20';
                        borderClass = 'border-green-400';
                        textClass = 'text-green-700 dark:text-green-300';
                      }
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border-l-4 ${bgClass} ${borderClass}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs font-semibold ${textClass}`}>
                              {segment.speaker || (isRecruiter ? 'Recruiter' : 'Candidate')}
                            </span>
                            {(segment.start !== undefined || segment.start_time !== undefined) && (
                              <span className="text-xs text-muted-foreground">
                                {Math.floor(segment.start || segment.start_time)}s
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-foreground">{segment.text}</p>
                        </div>
                      );
                    });
                  } else {
                    return <p className="whitespace-pre-wrap text-sm text-foreground">{displayTranscript}</p>;
                  }
                })()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick View Feedback Dialog */}
      <Dialog open={!!showFeedback} onOpenChange={() => setShowFeedback(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5 text-emerald-600" /> AI Feedback</DialogTitle>
            <DialogDescription>{showFeedback && `${showFeedback.call_type} call - ${formatDate(showFeedback.call_date)}`}</DialogDescription>
          </DialogHeader>
          {showFeedback && (
            <div className="space-y-4">
              {showFeedback.strengths?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-emerald-600 mb-2">Strengths</p>
                  <ul className="space-y-1">
                    {showFeedback.strengths.map((s, i) => (<li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />{s}</li>))}
                  </ul>
                </div>
              )}
              {showFeedback.improvement_areas?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-amber-600 mb-2">Areas for Improvement</p>
                  <ul className="space-y-1">
                    {showFeedback.improvement_areas.map((a, i) => (<li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />{a}</li>))}
                  </ul>
                </div>
              )}
              {showFeedback.coaching_tips?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-blue-600 mb-2">Coaching Tips</p>
                  <ul className="space-y-1">
                    {showFeedback.coaching_tips.map((t, i) => (<li key={i} className="text-sm text-muted-foreground flex items-start gap-2"><Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />{t}</li>))}
                  </ul>
                </div>
              )}
              {!showFeedback.strengths?.length && !showFeedback.improvement_areas?.length && (
                <p className="text-sm text-muted-foreground text-center py-4">No feedback available for this call.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Calls Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Delete {selectedCalls.length} Call(s)
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This will soft-delete the selected calls. They can be restored later from the Deleted Calls tab.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-foreground">Reason for Deletion *</Label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Please provide a reason for deleting these calls..."
                className="w-full mt-2 p-3 min-h-[100px] bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This reason will be recorded for audit purposes.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteReason('');
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteCalls}
              disabled={isDeleting || !deleteReason.trim()}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Calls
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reprocess Calls Dialog with Scoring Guide Selection */}
      <Dialog open={showReprocessDialog} onOpenChange={setShowReprocessDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-purple-600" />
              Reprocess Selected Calls
            </DialogTitle>
            <DialogDescription>
              {selectedCalls.length} call(s) selected for reprocessing. Choose a scoring guide and options below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Scoring Guide Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Scoring Guide *</Label>
              <Select value={selectedScoringGuide} onValueChange={setSelectedScoringGuide}>
                <SelectTrigger data-testid="reprocess-scoring-guide-select">
                  <SelectValue placeholder="Select scoring guide" />
                </SelectTrigger>
                <SelectContent>
                  {scoringGuides.map((guide) => (
                    <SelectItem key={guide.id} value={guide.id}>
                      <div className="flex items-center gap-2">
                        {guide.name}
                        {guide.is_default && (
                          <Badge variant="secondary" className="text-xs">Default</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The AI will use this scoring guide to evaluate all selected calls.
              </p>
            </div>
            
            {/* Reprocess Options */}
            <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
              <Label className="text-sm font-medium">Reprocess Options</Label>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="reprocess-transcript"
                  checked={reprocessOptions.reprocess_transcript}
                  onCheckedChange={(checked) => setReprocessOptions({...reprocessOptions, reprocess_transcript: checked})}
                />
                <Label htmlFor="reprocess-transcript" className="text-sm font-normal cursor-pointer">
                  Regenerate Transcript
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="reprocess-analysis"
                  checked={reprocessOptions.reprocess_analysis}
                  onCheckedChange={(checked) => setReprocessOptions({...reprocessOptions, reprocess_analysis: checked})}
                />
                <Label htmlFor="reprocess-analysis" className="text-sm font-normal cursor-pointer">
                  Regenerate AI Analysis
                </Label>
              </div>
            </div>
            
            {/* Info Notice */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Reprocessing will queue the calls for background analysis. You can track progress in the Process Tracker panel.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReprocessDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReprocessCalls}
              disabled={isReprocessing || !selectedScoringGuide}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-reprocess-btn"
            >
              {isReprocessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reprocess {selectedCalls.length} Call(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
